import { useCallback, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import ChevronDownIcon from "../../../assets/ChevronDownIcon";
import FilterDropdown from "../GlobalSearch/components/FilterDropdown";
import OrgSelect from "../../common/OrgSelect";
import { AccessibleOrganization } from "../../../store/slices/OrganizationsV3Slice";
import { getOrganizationName } from "../Organizations/utils";
import { UserListFilters } from "../../../services/UsersV3Service";
import { USER_ROLE_FILTER_OPTIONS, USER_STATUS_FILTER_OPTIONS } from "../../../constants/users";

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

interface UserFiltersProps {
  filters: UserListFilters;
  onChange: (filters: UserListFilters) => void;
  organizationOptions: AccessibleOrganization[];
  isOrganizationOptionsLoading: boolean;
  isOrganizationOptionsError: boolean;
}

// Role / Status / Organization dropdowns above the global user table. Role and
// Status are multi-select (empty = All); Organization is a single org and its
// sub-orgs, picked from the searchable hierarchy.
const UserFilters = ({
  filters,
  onChange,
  organizationOptions,
  isOrganizationOptionsLoading,
  isOrganizationOptionsError,
}: UserFiltersProps) => {
  const [openDropdown, setOpenDropdown] = useState<"role" | "status" | null>(null);

  const toggleRoleOpen = useCallback(
    () => setOpenDropdown((prev) => (prev === "role" ? null : "role")),
    []
  );
  const toggleStatusOpen = useCallback(
    () => setOpenDropdown((prev) => (prev === "status" ? null : "status")),
    []
  );

  const selectedOrganization = organizationOptions.find((org) => org.id === filters.organizationId);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <FilterDropdown
        label="Role"
        options={USER_ROLE_FILTER_OPTIONS}
        maxHeightClass="max-h-96"
        selectedValues={filters.role}
        onToggle={(value) => onChange({ ...filters, role: toggleValue(filters.role, value) })}
        onClearAll={() => onChange({ ...filters, role: [] })}
        isOpen={openDropdown === "role"}
        onToggleOpen={toggleRoleOpen}
      />
      <FilterDropdown
        label="Status"
        options={USER_STATUS_FILTER_OPTIONS}
        maxHeightClass="max-h-80"
        selectedValues={filters.status}
        onToggle={(value) => onChange({ ...filters, status: toggleValue(filters.status, value) })}
        onClearAll={() => onChange({ ...filters, status: [] })}
        isOpen={openDropdown === "status"}
        onToggleOpen={toggleStatusOpen}
      />
      <Popover className="relative">
        <PopoverButton
          onClick={() => setOpenDropdown(null)}
          className="flex max-w-[240px] items-center gap-1 rounded-lg border border-brColor px-3 py-2 text-sm font-semibold text-textColor3 shadow-light-sm hover:bg-gray-50 focus:outline-none"
        >
          <span className="truncate">{selectedOrganization ? getOrganizationName(selectedOrganization) : "Organization"}</span>
          <ChevronDownIcon />
        </PopoverButton>
        <PopoverPanel className="absolute right-0 z-20 mt-1 w-80 rounded-lg border border-[#e5e5e5] bg-white p-2 shadow-[0px_12px_20px_0px_rgba(0,0,0,0.1)]">
          {({ close }) => (
            <OrgSelect
              options={organizationOptions}
              value={filters.organizationId}
              onChange={(organizationId) => {
                onChange({ ...filters, organizationId });
                close();
              }}
              allLabel="All (Parent)"
              isLoading={isOrganizationOptionsLoading}
              isError={isOrganizationOptionsError}
            />
          )}
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default UserFilters;
